/**
 * Pure mapping between the VisionBilling proto messages (enum-typed, bigint counts,
 * Timestamp instants) and the auth-store `user` wire shape (lowercase status/tier
 * strings, Number counts, ISO instants) the billing UI reads (CreditsPage,
 * StorageUsageBar, GracePeriodBanner). Kept pure + isolated so it is independently
 * testable and the store stays unaware of the transport's typing.
 *
 * Billing enums flow inbound only: the client reads SubscriptionStatus / SubscriptionTier
 * from GetSubscriptionStatus and sends a plain plan_key string to StartSubscriptionCheckout
 * (no status/tier enum is ever sent), so the enum maps are fromProto-only. UNKNOWN is
 * rejected — the backend must never return it.
 */

import { timestampDate } from '@bufbuild/protobuf/wkt';
import {
  CreditTransactionType,
  SubscriptionStatus,
  SubscriptionTier,
} from '../gen/vision/billing/v1/vision_billing_pb';

// NONE (no subscription) and FREE (no paid tier) map to null — the auth-store `user`
// represents "no subscription" as null status/tier.
const STATUS_FROM_PROTO = {
  [SubscriptionStatus.NONE]: null,
  [SubscriptionStatus.ACTIVE]: 'active',
  [SubscriptionStatus.CANCELING]: 'canceling',
  [SubscriptionStatus.PAST_DUE]: 'past_due',
  [SubscriptionStatus.LAPSED]: 'lapsed',
};

const TIER_FROM_PROTO = {
  [SubscriptionTier.FREE]: null,
  [SubscriptionTier.SMALL]: 'small',
  [SubscriptionTier.MEDIUM]: 'medium',
  [SubscriptionTier.LARGE]: 'large',
  [SubscriptionTier.STORAGE_SMALL]: 'storage_small',
  [SubscriptionTier.STORAGE_MEDIUM]: 'storage_medium',
  [SubscriptionTier.STORAGE_LARGE]: 'storage_large',
};

function lookup(map, key, label) {
  // UNKNOWN (enum 0) is absent from the map; a present key may legitimately map to null.
  if (!Object.prototype.hasOwnProperty.call(map, key)) {
    throw new Error(`Unmappable ${label}: ${key}`);
  }
  return map[key];
}

function isoOrNull(timestamp) {
  return timestamp ? timestampDate(timestamp).toISOString() : null;
}

export function subscriptionStatusFromProto(result) {
  return {
    subscription_status: lookup(STATUS_FROM_PROTO, result.subscriptionStatus, 'subscription_status'),
    subscription_tier: lookup(TIER_FROM_PROTO, result.subscriptionTier, 'subscription_tier'),
    subscription_credits: Number(result.subscriptionCredits),
    purchased_credits: Number(result.purchasedCredits),
    trial_credits: Number(result.trialCredits ?? 0),
    available_trial_credits: Number(result.availableTrialCredits ?? 0),
    storage_used: Number(result.storageUsedBytes),
    storage_limit: Number(result.storageLimitBytes),
    subscription_period_end: isoOrNull(result.subscriptionPeriodEnd),
    grace_period_start: isoOrNull(result.gracePeriodStart),
    pending_plan_change: pendingPlanChangeFromProto(result.pendingPlanChange),
  };
}

// The pending scheduled up/downgrade, surfaced as the snake_case shape CreditsPage's
// pending-change banner reads. Absent (no scheduled change) maps to null.
function pendingPlanChangeFromProto(pending) {
  if (!pending) {
    return null;
  }
  const targetTier = lookup(TIER_FROM_PROTO, pending.targetTier, 'subscription_tier');
  if (targetTier === null) {
    throw new Error(`Unmappable target subscription_tier in pending change: ${pending.targetTier}`);
  }
  return {
    target_tier: targetTier,
    target_plan_name: pending.targetPlanName,
    target_credits: Number(pending.targetCredits),
    effective_date: isoOrNull(pending.effectiveDate),
  };
}

export function storageBreakdownFromProto(result) {
  return {
    storage_used: Number(result.storageUsedBytes),
    storage_limit: Number(result.storageLimitBytes),
    projects: result.projects.map((project) => ({
      id: project.projectId,
      name: project.name,
      storage_used: Number(project.storageUsedBytes),
    })),
    personal_storage: Number(result.personalStorageBytes),
  };
}

export function recalculateResultFromProto(result) {
  return {
    storage_used: Number(result.storageUsedBytes),
    storage_limit: Number(result.storageLimitBytes),
  };
}

export function changePlanResultFromProto(result) {
  const targetTier = lookup(TIER_FROM_PROTO, result.targetTier, 'subscription_tier');
  if (targetTier === null) {
    throw new Error(`Unmappable target subscription_tier in change result: ${result.targetTier}`);
  }
  return {
    target_tier: targetTier,
    effective_date: isoOrNull(result.effectiveDate),
  };
}

export function cancelSubscriptionResultFromProto(result) {
  return {
    subscription_status: lookup(STATUS_FROM_PROTO, result.subscriptionStatus, 'subscription_status'),
    subscription_period_end: isoOrNull(result.subscriptionPeriodEnd),
  };
}

export function billingPortalFromProto(result) {
  return { portalUrl: result.portalUrl };
}

export function checkoutFromProto(result) {
  return {
    checkoutUrl: result.checkoutUrl,
    sessionId: result.sessionId,
  };
}

export function plansFromProto(result) {
  return result.plans.map((plan) => {
    const tier = lookup(TIER_FROM_PROTO, plan.tier, 'subscription_tier');
    if (tier === null) {
      throw new Error(`Unmappable subscription_tier in plan: ${plan.tier}`);
    }
    // Storage labels are denominated in GiB (powers of 1024) and shown as "GB" —
    // intentional; do not convert to SI gigabytes.
    const storageGib = Math.round(Number(plan.storageLimitBytes) / (1024 * 1024 * 1024));
    return {
      id: plan.planKey,
      name: plan.name,
      tier,
      price_label: plan.priceLabel,
      credits: Number(plan.subscriptionCredits),
      storage_label: `${storageGib} GB`,
      sort_order: plan.sortOrder,
    };
  });
}

const TX_TYPE_FROM_PROTO = {
  [CreditTransactionType.SUBSCRIPTION_GRANT]: 'subscription_grant',
  [CreditTransactionType.PURCHASE]: 'pack_purchase',
  [CreditTransactionType.TRANSFER_TO_PROJECT]: 'transfer_to_project',
  [CreditTransactionType.PROJECT_PACK_PURCHASE]: 'project_pack_purchase',
  [CreditTransactionType.PROJECT_REFUND]: 'project_refund',
};

export function packsFromProto(result) {
  return result.packs.map((pack) => ({
    id: pack.packKey,
    credits: Number(pack.credits),
    price_label: pack.priceLabel,
  }));
}

export function transactionsFromProto(result) {
  return result.transactions.map((tx) => ({
    id: tx.id,
    type: TX_TYPE_FROM_PROTO[tx.transactionType] ?? 'unknown',
    amount: Number(tx.amount),
    created_at: tx.createdAt ? timestampDate(tx.createdAt).toISOString() : null,
  }));
}
